import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AvailabilitySchedule.css';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Dom', fullLabel: 'Domingo' },
  { value: 1, label: 'Lun', fullLabel: 'Lunes' },
  { value: 2, label: 'Mar', fullLabel: 'Martes' },
  { value: 3, label: 'Mié', fullLabel: 'Miércoles' },
  { value: 4, label: 'Jue', fullLabel: 'Jueves' },
  { value: 5, label: 'Vie', fullLabel: 'Viernes' },
  { value: 6, label: 'Sáb', fullLabel: 'Sábado' },
];

const AvailabilitySchedule = ({ value = null, onChange, label = 'Disponibilidad' }) => {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState(value?.days || []);
  const [timeSlots, setTimeSlots] = useState(value?.timeSlots || []);
  const [allDay, setAllDay] = useState(!value?.timeSlots || value?.timeSlots?.length === 0);
  const [alwaysAvailable, setAlwaysAvailable] = useState(
    (!value?.days || value?.days?.length === 0) &&
      (!value?.timeSlots || value?.timeSlots?.length === 0)
  );

  useEffect(() => {
    if (onChange) {
      if (alwaysAvailable) {
        // Si está siempre disponible, no enviar availability
        onChange(null);
      } else {
        onChange({
          days: selectedDays,
          timeSlots: allDay ? [] : timeSlots,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, timeSlots, allDay, alwaysAvailable]);

  const handleDayToggle = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  };

  const handleAddTimeSlot = () => {
    setTimeSlots((prev) => [...prev, { start: '12:00', end: '15:00' }]);
  };

  const handleRemoveTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index, field, value) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot))
    );
  };

  const handleAlwaysAvailableToggle = () => {
    const newValue = !alwaysAvailable;
    setAlwaysAvailable(newValue);
    if (newValue) {
      // Reset all settings
      setSelectedDays([]);
      setTimeSlots([]);
      setAllDay(true);
    } else {
      // Set default: all days, all day
      setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
      setAllDay(true);
    }
  };

  const handleAllDayToggle = () => {
    const newAllDay = !allDay;
    setAllDay(newAllDay);
    if (newAllDay) {
      setTimeSlots([]);
    } else if (timeSlots.length === 0) {
      setTimeSlots([{ start: '12:00', end: '15:00' }]);
    }
  };

  return (
    <div className="availability-schedule">
      {label && <label className="availability-label">{label}</label>}

      {/* Siempre disponible checkbox */}
      <div className="availability-option">
        <label className="checkbox-label">
          <input type="checkbox" checked={alwaysAvailable} onChange={handleAlwaysAvailableToggle} />
          <span>Siempre disponible (sin restricciones)</span>
        </label>
        <p className="help-text">El producto estará visible todo el tiempo</p>
      </div>

      {!alwaysAvailable && (
        <>
          {/* Días de la semana */}
          <div className="days-section">
            <label className="section-label">Días disponibles</label>
            <div className="days-grid" role="group" aria-label={t('menu.availableDays')}>
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  className={`day-button ${selectedDays.includes(day.value) ? 'active' : ''}`}
                  onClick={() => handleDayToggle(day.value)}
                  title={day.fullLabel}
                  aria-label={day.fullLabel}
                  aria-pressed={selectedDays.includes(day.value)}
                >
                  {day.label}
                </button>
              ))}
            </div>
            {selectedDays.length === 0 && (
              <p className="help-text warning">Selecciona al menos un día</p>
            )}
          </div>

          {/* Todo el día checkbox */}
          <div className="availability-option">
            <label className="checkbox-label">
              <input type="checkbox" checked={allDay} onChange={handleAllDayToggle} />
              <span>Disponible todo el día</span>
            </label>
          </div>

          {/* Horarios específicos */}
          {!allDay && (
            <div className="timeslots-section">
              <label className="section-label">Horarios</label>
              {timeSlots.map((slot, index) => (
                <div key={index} className="timeslot-row">
                  <input
                    type="time"
                    value={slot.start}
                    onChange={(e) => handleTimeSlotChange(index, 'start', e.target.value)}
                    className="time-input"
                    aria-label={t('menu.timeSlotStart', { index: index + 1 })}
                  />
                  <span className="time-separator">a</span>
                  <input
                    type="time"
                    value={slot.end}
                    onChange={(e) => handleTimeSlotChange(index, 'end', e.target.value)}
                    className="time-input"
                    aria-label={t('menu.timeSlotEnd', { index: index + 1 })}
                  />
                  {timeSlots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTimeSlot(index)}
                      className="remove-timeslot-btn"
                      title="Eliminar horario"
                      aria-label={t('menu.removeTimeSlot', { index: index + 1 })}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={handleAddTimeSlot} className="add-timeslot-btn">
                + Agregar horario
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AvailabilitySchedule;
